/**
 * Legacy Gift Card Redemption
 * 
 * Detects legacy_token parameter on account page load and automatically
 * initiates the redemption flow via API call.
 * 
 * Uses toast notifications to show processing, success, and error states.
 * 
 * Uses existing project helpers:
 * - window.apiService for API calls
 * - window.proxyPath for app proxy endpoint
 * - Shopify.formatMoney() for currency formatting
 * - TranslationUtils.get() for translations
 * - toastNotifications for all notifications
 * - Dispatches 'voucher-action-completed' event to refresh gift cards cache
 */

(function() {
  'use strict';

  /**
   * Extract legacy_token from URL query parameters
   * @returns {string|null} Token or null if not present
   */
  function getTokenFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('legacy_token');
  }

  /**
   * Remove legacy_token from URL without page reload
   */
  function cleanupUrl() {
    const url = new URL(window.location);
    url.searchParams.delete('legacy_token');
    window.history.replaceState({}, document.title, url.pathname + url.search);
  }

  /**
   * Call the redemption API using ApiService
   * @param {string} token - The legacy token to redeem
   * @returns {Promise<Object>} API response
   */
  async function callRedemptionApi(token) {
    const locale = window.Shopify?.locale || '';
    const endpoint = `${window.proxyPath}/legacy-giftcard/redeem/${encodeURIComponent(token)}`;

    const options = {};
    if (locale) {
      options.headers = { 'Accept-Language': locale };
    }

    try {
      const data = await window.apiService.post(endpoint, null, options);
      
      // Normalize error code: use 'reason' field if 'errorCode' is not present
      if (!data.success && data.reason && !data.errorCode) {
        data.errorCode = data.reason;
      }
      
      return data;
    } catch (error) {
      // Handle authentication redirect (401)
      if (error.status === 401 && error.response?.redirect_url) {
        window.location.href = error.response.redirect_url;
        return { success: false, message: 'Redirecting to login...', errorCode: 'REDIRECTING' };
      }
      
      // ApiService throws ApiError for non-200 responses, but the response might contain valid error data
      // Extract the error data from the response if available
      if (error.response && typeof error.response === 'object') {
        const errorData = error.response;
        
        // Normalize error code: use 'reason' field if 'errorCode' is not present
        if (!errorData.errorCode && errorData.reason) {
          errorData.errorCode = errorData.reason;
        }
        
        // Return the structured error response instead of throwing
        return {
          success: false,
          message: errorData.message || error.message,
          errorCode: errorData.errorCode || 'GENERIC_ERROR',
          ...errorData
        };
      }
      
      // Re-throw for actual network/connection errors
      throw error;
    }
  }

  /**
   * Handle successful redemption
   * @param {Object} response - Success response from API
   */
  function handleSuccess(response) {
    const { giftCard } = response;

    // Build success message with amount
    let successMessage = TranslationUtils.get(
      'customer.account.legacy_giftcard.success_title',
      'Gift Card Redeemed!'
    );

    if (giftCard && giftCard.initial_value) {
      const formattedAmount = formatMoney(giftCard.initial_value);
      successMessage += ` ${formattedAmount}`;
    }

    // Show success toast that stays until user closes it (duration = 0)
    if (typeof toastNotifications !== 'undefined') {
      toastNotifications.success(successMessage, 0);
    }

    // Notify cache invalidation to refresh gift cards list
    const event = new CustomEvent('voucher-action-completed', {
      detail: { action: 'redeem' }
    });
    document.dispatchEvent(event);
  }

  /**
   * Handle redemption error
   * @param {Object} response - Error response
   */
  function handleError(response) {
    const { errorCode, message } = response;
    const errorMessage = getErrorMessage(errorCode, message);

    // Show toast notification for all errors
    // Use info toast for informational errors like already_used
    if (typeof toastNotifications !== 'undefined') {
      if (errorCode === 'already_used') {
        toastNotifications.info(errorMessage);
      } else {
        toastNotifications.error(errorMessage);
      }
    }
  }

  /**
   * Get translated error message based on error code
   * @param {string} errorCode - Error code from API
   * @param {string} fallback - Fallback message
   * @returns {string} Translated error message
   */
  function getErrorMessage(errorCode, fallback) {
    const errorKey = `customer.account.legacy_giftcard.error_${errorCode?.toLowerCase() || 'generic'}`;
    
    return TranslationUtils.get(
      errorKey,
      fallback ||
      TranslationUtils.get('customer.account.legacy_giftcard.error_generic') ||
      'An error occurred. Please try again or contact support.'
    );
  }

  /**
   * Format money using Shopify's formatMoney
   * @param {number} amount - Amount to format
   * @returns {string} Formatted currency string
   */
  function formatMoney(amount) {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return amount;

    const cents = Math.round(numAmount * 100);
    const moneyFormat = window.theme?.moneyFormat || '{{amount}} kr';
    return Shopify.formatMoney(cents, moneyFormat);
  }

  /**
   * Start the redemption process
   */
  async function startRedemption(token) {
    // Show processing toast
    const processingMessage = TranslationUtils.get(
      'customer.account.legacy_giftcard.loading_title',
      'Processing Your Gift Card'
    );
    if (typeof toastNotifications !== 'undefined') {
      toastNotifications.info(processingMessage);
    }

    try {
      const response = await callRedemptionApi(token);
      
      if (response.success) {
        handleSuccess(response);
        // Only clean up URL on success
        cleanupUrl();
      } else {
        handleError(response);
        // Keep token in URL on error so user can refresh/retry
      }
    } catch (error) {
      console.error('Legacy Gift Card Redemption Error:', error);
      handleError({
        success: false,
        message: TranslationUtils.get('customer.account.legacy_giftcard.network_error', 'Connection error'),
        errorCode: 'NETWORK_ERROR'
      });
      // Keep token in URL on error so user can refresh/retry
    }
  }

  // Wait for DOM and global dependencies to be ready
  function init() {
    const token = getTokenFromUrl();
    if (token) {
      startRedemption(token);
    }
  }

  // Run after DOM is loaded to ensure toastNotifications is available
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded, run immediately
    init();
  }
})();
